import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Label } from './Label';
import TextInput from './TextInput';

interface IconPickerProps {
  value?: string;
  onChange?: (value: string) => void;
  name?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
}

// Predefined list of commonly used icons (emojis)
const ICON_LIST = [
  // Folders & Files
  '📁', '📂', '📄', '📝', '📋', '📊', '📈', '📉', '📑', '📃',
  // Security & Lock
  '🔒', '🔓', '🔐', '🔑', '🛡️', '🔐', '🔒', '🔓',
  // Communication
  '📧', '✉️', '📮', '📬', '📭', '📨', '💬', '💌',
  // Technology
  '💻', '🖥️', '⌨️', '🖱️', '🖨️', '📱', '📞', '☎️', '📠', '💾', '💿', '📀',
  // Business & Finance
  '💰', '💵', '💴', '💶', '💷', '💸', '💳', '🧾', '📈', '📉', '💼', '🏦',
  // Network & Web
  '🌐', '🔗', '🔍', '🔎', '📡', '📶', '📲', '📱',
  // Documents & Notes
  '📔', '📕', '📗', '📘', '📙', '📚', '📓', '📒', '📃', '📜', '📰', '🗞️',
  // Tools & Settings
  '⚙️', '🔧', '🔨', '🛠️', '🔩', '⚡', '🔌', '💡', '🔦', '🕯️',
  // Time & Calendar
  '📅', '📆', '🗓️', '⏰', '⏲️', '⏱️', '🕐', '🕑', '🕒', '🕓', '🕔', '🕕',
  // People & Contacts
  '👤', '👥', '👨‍💼', '👩‍💼', '👨‍💻', '👩‍💻', '👨‍🔧', '👩‍🔧', '👨‍🔬', '👩‍🔬',
  // Location & Maps
  '📍', '🗺️', '🌍', '🌎', '🌏', '🏠', '🏢', '🏭', '🏪', '🏫',
  // Shopping & E-commerce
  '🛒', '🛍️', '🛎️', '🏪', '🏬', '🏨', '🏩',
  // Health & Medical
  '🏥', '💊', '💉', '🩺', '🚑', '⚕️',
  // Education
  '🎓', '📚', '📖', '📗', '📘', '📙', '📕', '📔', '📒', '📓',
  // Entertainment
  '🎬', '🎭', '🎨', '🎪', '🎯', '🎲', '🎮', '🕹️',
  // Food & Drink
  '🍕', '🍔', '🍟', '🌭', '🍿', '🧂', '🥓', '🥩', '🍗', '🍖',
  // Travel & Transport
  '✈️', '🚀', '🚁', '🚂', '🚃', '🚄', '🚅', '🚆', '🚇', '🚈', '🚉', '🚊',
  // Weather
  '☀️', '⛅', '☁️', '🌧️', '⛈️', '🌩️', '❄️', '☃️', '⛄', '🌨️',
  // Symbols & Shapes
  '⭐', '🌟', '💫', '✨', '⚡', '🔥', '💥', '✅', '❌', '⚠️', 'ℹ️', '❓', '❔', '❗', '❕',
  // Objects
  '📦', '📮', '📯', '📪', '📬', '📭', '📧', '✉️', '📨', '📩',
  // Miscellaneous
  '🎯', '🎲', '🎪', '🎨', '🎭', '🎬', '🎤', '🎧', '🎵', '🎶', '🎼', '🎹', '🥁', '🎷', '🎺', '🎸',
];

/**
 * IconPicker - Icon selection component with predefined emoji icons
 * Displays a searchable grid of icons for easy selection
 */
export const IconPicker: React.FC<IconPickerProps> = ({
  value = '',
  onChange,
  name,
  label,
  required = false,
  disabled = false,
  readOnly = false,
  error,
  helperText,
  className = '',
}) => {
  const { colors } = useTheme();
  const [selectedIcon, setSelectedIcon] = useState(value || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    setSelectedIcon(value || '');
  }, [value]);

  // Filter icons based on search term
  const filteredIcons = ICON_LIST.filter((icon) => {
    if (!searchTerm) return true;
    // You could add more sophisticated search logic here
    return true; // For now, show all icons
  });

  const handleIconSelect = (icon: string) => {
    if (disabled || readOnly) return;
    setSelectedIcon(icon);
    onChange?.(icon);
    setShowPicker(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    if (disabled || readOnly) return;
    setSelectedIcon('');
    onChange?.('');
  };

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <Label htmlFor={name} required={required}>
          {label}
        </Label>
      )}
      
      <div className="space-y-2">
        {/* Selected Icon Display */}
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'w-12 h-12 rounded-lg border-2 flex items-center justify-center text-2xl transition-all',
              colors.border,
              selectedIcon && cn(colors.primaryBorderAll, 'ring-2', colors.surfaceHover),
              disabled || readOnly ? 'opacity-50 cursor-not-allowed' : cn('cursor-pointer', colors.interactiveHover),
              error ? 'border-red-500' : ''
            )}
            onClick={() => !disabled && !readOnly && setShowPicker(!showPicker)}
            title={selectedIcon || 'Click to select an icon'}
          >
            {selectedIcon || '📁'}
          </div>
          
          {selectedIcon && !disabled && !readOnly && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md border transition-colors',
                colors.error,
                colors.border,
                colors.interactiveHover
              )}
            >
              Clear
            </button>
          )}
          
          {!disabled && !readOnly && (
            <button
              type="button"
              onClick={() => setShowPicker(!showPicker)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md border transition-colors',
                colors.surface,
                colors.text,
                colors.border,
                colors.interactiveHover
              )}
            >
              {showPicker ? 'Hide Icons' : 'Browse Icons'}
            </button>
          )}
        </div>

        {/* Icon Picker Grid */}
        {showPicker && !disabled && !readOnly && (
          <div
            className={cn(
              'border rounded-lg p-4 transition-colors',
              colors.border,
              colors.surface,
              error ? 'border-red-500' : ''
            )}
          >
            {/* Search Input */}
            <div className="mb-4">
              <TextInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search icons..."
                size="sm"
                showClearButton={true}
              />
            </div>

            {/* Icon Grid */}
            <div className="max-h-64 overflow-y-auto">
              <div className="grid grid-cols-10 gap-2">
                {filteredIcons.map((icon, index) => (
                  <button
                    key={`${icon}-${index}`}
                    type="button"
                    onClick={() => handleIconSelect(icon)}
                    className={cn(
                      'w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xl transition-all',
                      'hover:scale-110',
                      colors.interactiveHover,
                      'focus:outline-none focus:ring-2 focus:ring-offset-1',
                      colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
                      selectedIcon === icon
                        ? cn(colors.primaryBorderAll, 'ring-2', colors.surfaceHover, 'scale-110')
                        : colors.border,
                      'cursor-pointer'
                    )}
                    title={icon}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Custom Icon Input */}
        {!disabled && !readOnly && (
          <div className="mt-2">
            <TextInput
              value={selectedIcon}
              onChange={(e) => {
                setSelectedIcon(e.target.value);
                onChange?.(e.target.value);
              }}
              placeholder="Or enter custom emoji/icon"
              size="sm"
              helperText="Enter any emoji or icon character"
            />
          </div>
        )}

        {/* Error or helper text */}
        {(error || helperText) && (
          <div className={cn('text-xs mt-1', error ? colors.error : colors.textMuted)}>
            {error || helperText}
          </div>
        )}
      </div>
    </div>
  );
};

export default IconPicker;

